import { type StackProps, type Theme, useTheme } from "@mui/material";
import {
    FoodsSelect,
    FoodsSelectServerPagination,
    type FoodsSelectProps,
    type SelectWrapperWithServerPaginationProps,
} from "@foodpilot/foods";
import { useId } from "react";

import { FoodsField } from "./FoodsFieldPrimitives";
import { FoodsSelectTriggerAdapter } from "./FoodsSelectTriggerAdapter";

export type FoodsSelectFieldProps<T> = Pick<
    FoodsSelectProps<T>,
    "selectedOption" | "options" | "onChange" | "getId" | "getName" | "getIcon"
> & {
    label?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    placeholder: string;
    searchPlaceholder: string;
    emptyOptionsText: string;
    noResultText: string;
    noValue?: string;
    disabled?: boolean;
    disableSearch?: boolean;
    closeOnSelect?: boolean;
    appearance?: "default" | "product-v2";
    accessibleLabel?: string;
    stackProps?: StackProps;
    boxOptionsProps?: FoodsSelectProps<T>["BoxOptionsProps"];
};

export const FoodsSelectField = <T,>({
    label,
    required = false,
    helperText,
    errorText,
    placeholder,
    searchPlaceholder,
    emptyOptionsText,
    noResultText,
    noValue,
    disabled = false,
    disableSearch = false,
    closeOnSelect = true,
    appearance = "default",
    accessibleLabel,
    stackProps,
    boxOptionsProps,
    ...selectProps
}: FoodsSelectFieldProps<T>) => {
    const generatedId = useId();
    const isProductV2 = appearance === "product-v2";
    const labelId = isProductV2 && label !== undefined ? `foods-select-label-${generatedId}` : undefined;
    const descriptionId =
        isProductV2 && (errorText || helperText) ? `foods-select-description-${generatedId}` : undefined;
    const select = (
        <FoodsSelect<T>
            {...selectProps}
            disabled={disabled}
            disableSearch={disableSearch}
            closeOnSelect={closeOnSelect}
            noValue={noValue}
            textOptions={{
                placeholder,
                searchPlaceholder,
                emptyOptionsText,
                noResult: noResultText,
            }}
            BoxProps={{ width: "100%" }}
            BoxOptionsProps={boxOptionsProps}
        />
    );

    return (
        <FoodsField
            label={label}
            required={required}
            helperText={helperText}
            errorText={errorText}
            labelId={labelId}
            descriptionId={descriptionId}
            announceError={isProductV2}
            stackProps={stackProps}
        >
            {isProductV2 ?
                <FoodsSelectTriggerAdapter
                    labelId={labelId}
                    accessibleLabel={accessibleLabel}
                    descriptionId={descriptionId}
                    invalid={Boolean(errorText)}
                >
                    {select}
                </FoodsSelectTriggerAdapter>
            :   select}
        </FoodsField>
    );
};

export type FoodsServerSelectFieldProps<T> = Pick<
    SelectWrapperWithServerPaginationProps<T>,
    | "selectedOption"
    | "options"
    | "onChange"
    | "getId"
    | "getName"
    | "getIcon"
    | "search"
    | "setSearch"
    | "isSearchFound"
    | "infiniteScroll"
> & {
    filterControls?: SelectWrapperWithServerPaginationProps<T>["filterControls"];
    label?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    placeholder: string;
    searchPlaceholder: string;
    emptyOptionsText: string;
    noResultText: string;
    defaultValueText: string;
    noValue?: string;
    disabled?: boolean;
    closeOnSelect?: boolean;
    appearance?: "default" | "product-v2";
    accessibleLabel?: string;
    stackProps?: StackProps;
    boxOptionsProps?: SelectWrapperWithServerPaginationProps<T>["BoxOptionsProps"];
};

/** Product V2 closed-select surface shared with the canonical Brand field. */
export const getProductV2SelectFieldStyles = (theme: Theme, hasSelectedOption: boolean) => ({
    "& .MuiButton-select": {
        height: theme.spacing(5),
        padding: theme.spacing(0, 1.5),
        "& .MuiTypography-root": {
            ...theme.typography["body-regular"],
            color: hasSelectedOption ? theme.custom.grey[2800] : theme.custom.grey[1400],
            fontFamily: theme.typography.fontFamily,
            marginLeft: theme.spacing(1),
        },
        "& .MuiSvgIcon-root": {
            height: theme.spacing(2.5),
            width: theme.spacing(2.5),
        },
    },
});

/** Canonical server-backed FOODS select field used by Product V2 forms. */
export const FoodsServerSelectField = <T,>({
    label,
    required = false,
    helperText,
    errorText,
    placeholder,
    searchPlaceholder,
    emptyOptionsText,
    noResultText,
    defaultValueText,
    noValue,
    disabled = false,
    closeOnSelect = true,
    appearance = "default",
    accessibleLabel,
    stackProps,
    boxOptionsProps,
    selectedOption,
    ...selectProps
}: FoodsServerSelectFieldProps<T>) => {
    const theme = useTheme();
    const generatedId = useId();
    const isProductV2 = appearance === "product-v2";
    const labelId = isProductV2 && label !== undefined ? `foods-server-select-label-${generatedId}` : undefined;
    const descriptionId =
        isProductV2 && (errorText || helperText) ? `foods-server-select-description-${generatedId}` : undefined;
    const productV2FieldStyles = isProductV2 ? getProductV2SelectFieldStyles(theme, selectedOption !== null) : {};
    const select = (
        <FoodsSelectServerPagination<T>
            {...selectProps}
            selectedOption={selectedOption}
            disabled={disabled}
            closeOnSelect={closeOnSelect}
            noValue={noValue}
            textOptions={{
                placeholder,
                searchPlaceholder,
                emptyOptionsText,
                noResult: noResultText,
                defaultValueText,
            }}
            BoxProps={{ width: "100%", ...productV2FieldStyles }}
            BoxOptionsProps={{ width: theme.spacing(56.25), ...boxOptionsProps }}
        />
    );

    return (
        <FoodsField
            label={label}
            required={required}
            helperText={helperText}
            errorText={errorText}
            labelId={labelId}
            descriptionId={descriptionId}
            announceError={isProductV2}
            stackProps={stackProps}
        >
            {isProductV2 ?
                <FoodsSelectTriggerAdapter
                    labelId={labelId}
                    accessibleLabel={accessibleLabel}
                    descriptionId={descriptionId}
                    invalid={Boolean(errorText)}
                >
                    {select}
                </FoodsSelectTriggerAdapter>
            :   select}
        </FoodsField>
    );
};
